/**
 * Core API - Articles (Products)
 *
 * Database operations for articles/products that are manufactured in the factory.
 * Articles can be simple (just a code) or detailed (code, name, description).
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface Article {
  id: string;
  tenant_id: string;
  factory_id: string;
  code: string;
  name: string | null;
  description: string | null;
  area_id: string | null;
  line_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ArticleWithHierarchy extends Article {
  area?: { id: string; name: string } | null;
  line?: { id: string; name: string } | null;
}

/**
 * Article with a relevance score for smart filtering.
 * Higher score = more relevant to the selected hierarchy.
 */
export interface ArticleWithRelevance extends Article {
  relevanceScore: number;
}

export interface CreateArticleInput {
  tenant_id: string;
  factory_id: string;
  code: string;
  name?: string | null;
  description?: string | null;
  area_id?: string | null;
  line_id?: string | null;
}

// =============================================================================
// Hierarchy Matching Utilities
// =============================================================================

/**
 * Calculates a relevance score for an article based on how well it matches
 * the selected hierarchy. Higher score = better match.
 * 
 * Scoring logic:
 * - Exact line match: 100 points
 * - Exact area match (article has no line or line matches): 50 points
 * - Factory-wide article (null area & line): 10 points
 * - Area match when user selected a line in that area: 25 points
 * - No match (different area/line): 0 points (filtered out)
 */
export function calculateArticleRelevance(
  article: Article,
  selectedAreaId: string | null | undefined,
  selectedLineId: string | null | undefined
): number {
  const articleAreaId = article.area_id;
  const articleLineId = article.line_id;

  // Factory-wide article (no area or line restriction)
  if (!articleAreaId && !articleLineId) {
    return 10;
  }

  // No hierarchy selected - all articles are relevant
  if (!selectedAreaId && !selectedLineId) {
    return 50;
  }

  // Line is selected
  if (selectedLineId) {
    // Exact line match
    if (articleLineId === selectedLineId) {
      return 100;
    }
    // Article is for the area (no line restriction), and area matches
    if (!articleLineId && articleAreaId === selectedAreaId) {
      return 50;
    }
    // Article is area-only and matches the area of the selected line
    // (This requires knowing the area of the selected line, which we get from props)
    if (!articleLineId && selectedAreaId && articleAreaId === selectedAreaId) {
      return 50;
    }
    // Factory-wide article is still compatible
    if (!articleAreaId && !articleLineId) {
      return 10;
    }
    // Different line but same area - still show but lower priority
    if (articleAreaId === selectedAreaId) {
      return 25;
    }
    // No match
    return 0;
  }

  // Only area is selected (no line)
  if (selectedAreaId) {
    // Exact area match (article has no line or any line in this area)
    if (articleAreaId === selectedAreaId) {
      return articleLineId ? 75 : 100; // Area-only articles rank higher
    }
    // Factory-wide article
    if (!articleAreaId && !articleLineId) {
      return 10;
    }
    // No match
    return 0;
  }

  return 0;
}

/**
 * Filters and sorts articles based on hierarchy relevance.
 * Returns articles that are compatible with the selected hierarchy,
 * sorted by relevance (best matches first).
 */
export function filterAndSortArticlesByRelevance(
  articles: Article[],
  selectedAreaId: string | null | undefined,
  selectedLineId: string | null | undefined
): ArticleWithRelevance[] {
  return articles
    .map((article) => ({
      ...article,
      relevanceScore: calculateArticleRelevance(article, selectedAreaId, selectedLineId),
    }))
    .filter((article) => article.relevanceScore > 0)
    .sort((a, b) => {
      // Sort by relevance score (descending), then by code (ascending)
      if (b.relevanceScore !== a.relevanceScore) {
        return b.relevanceScore - a.relevanceScore;
      }
      return a.code.localeCompare(b.code);
    });
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches all active articles for a factory, ordered by code.
 */
export async function fetchArticlesByFactory(factoryId: string): Promise<Article[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('articles')
    .select('*')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('code', { ascending: true });

  if (error) throw error;
  return data;
}

/**
 * Fetches articles with hierarchy information (area, line).
 */
export async function fetchArticlesWithHierarchy(factoryId: string): Promise<ArticleWithHierarchy[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('articles')
    .select(`
      *,
      area:factory_areas(id, name),
      line:production_lines(id, name)
    `)
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('code', { ascending: true });

  if (error) throw error;
  return data as unknown as ArticleWithHierarchy[];
}

/**
 * Fetches articles for a specific hierarchy level with smart matching.
 * 
 * The matching logic is inclusive rather than exclusive:
 * - Articles with exact line match get highest priority
 * - Articles with area match (no line) get medium priority
 * - Factory-wide articles (no area/line) get lower priority
 * - Articles from different areas are excluded
 * 
 * Results are sorted by relevance, then by code.
 */
export async function fetchArticlesByHierarchy(
  factoryId: string,
  areaId?: string | null,
  lineId?: string | null
): Promise<ArticleWithRelevance[]> {
  // Fetch all active articles for the factory
  const { data, error } = await supabase
    .schema('core')
    .from('articles')
    .select('*')
    .eq('factory_id', factoryId)
    .eq('is_active', true);

  if (error) throw error;

  // Apply smart filtering and sorting
  return filterAndSortArticlesByRelevance(data, areaId, lineId);
}

/**
 * Searches articles by code or name, with optional hierarchy filtering.
 * Optimized for large datasets (100k+ records) - uses server-side filtering.
 * Results are filtered by relevance to the selected hierarchy and sorted
 * with best matches first.
 */
export async function searchArticles(
  factoryId: string,
  searchTerm: string,
  areaId?: string | null,
  lineId?: string | null
): Promise<ArticleWithRelevance[]> {
  // Sanitize search term to prevent SQL injection via ilike
  const sanitized = searchTerm.replace(/[%_]/g, '\\$&');
  
  const { data, error } = await supabase
    .schema('core')
    .from('articles')
    .select('*')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .or(`code.ilike.%${sanitized}%,name.ilike.%${sanitized}%`)
    .order('code', { ascending: true })
    .limit(50);

  if (error) throw error;

  // Apply smart filtering and sorting
  return filterAndSortArticlesByRelevance(data, areaId, lineId);
}

/**
 * Fetches a single article by ID.
 */
export async function fetchArticleById(articleId: string): Promise<Article | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('articles')
    .select('*')
    .eq('id', articleId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Creates a new article.
 */
export async function createArticle(input: CreateArticleInput): Promise<Article> {
  const { data, error } = await supabase
    .schema('core')
    .from('articles')
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates an existing article.
 */
export async function updateArticle(
  articleId: string,
  updates: Partial<Omit<CreateArticleInput, 'tenant_id' | 'factory_id'>>
): Promise<Article> {
  const { data, error } = await supabase
    .schema('core')
    .from('articles')
    .update(updates)
    .eq('id', articleId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Soft-deletes an article (sets is_active = false).
 */
export async function deleteArticle(articleId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('articles')
    .update({ is_active: false })
    .eq('id', articleId);

  if (error) throw error;
}
